import type { Analysis } from '@ni/shared';
import { analysisCaveats, analysisOfKind, analysisProse } from '../../lib/analysis-copy';
import {
  PROTECTIVE_READ_HEADS,
  PROTECTIVE_READ_SUBS,
  type ProtectiveReadKind,
  type StatusForAnnotation,
} from '../../lib/copy';
import { Caveats } from '../ui/caveats';
import { Prose } from '../ui/prose';
import { KeptFromEarlier } from './kept-from-earlier';
import { Section } from '../ui/section';

/**
 * **Exposure brief** and **Case read** — the security vertical's two readings
 * (security fold, 2026-09-22), and the fourth block in the reading column beside
 * Fit, Relationship and Fit and interest.
 *
 * Both carry `relationship_read`'s content contract and nothing else: prose and
 * optional caveats. There is deliberately no score, no level and no list —
 * neither kind may infer intent, name a threat or attach a number, so there is
 * nothing here for a grid or a meter to render, and adding one would be the page
 * asserting a judgement nobody made.
 *
 * Built on the rule its three neighbours are built on: **no analysis, no
 * block.** Nothing is gated on `org.vertical` — the presence of the reading is
 * the gate, which stays right for a record whose status changed after the
 * reading was written, and for an associate who has neither.
 */
export function ProtectiveReadBlock({
  kind,
  analyses,
  subject,
}: {
  kind: ProtectiveReadKind;
  analyses: Analysis[];
  /** The status block behind the kept-from-earlier line; absent, no line. */
  subject?: StatusForAnnotation;
}) {
  const reading = analysisOfKind(analyses, kind);
  if (!reading) return null;

  const prose = analysisProse(reading);

  return (
    <Section lead title={PROTECTIVE_READ_HEADS[kind]}>
      <div className="sub">{PROTECTIVE_READ_SUBS[kind]}</div>
      <KeptFromEarlier subject={subject} kind={kind} />

      {prose ? <Prose text={prose} className="notes" /> : null}

      <Caveats caveats={analysisCaveats(reading)} />
    </Section>
  );
}
